import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { usePaletteStore, type PaletteCommand } from '@repletoryreach/sdk-utils/store';
import { useShortcut, useStableId } from '@repletoryreach/sdk-utils/hooks';
import { Portal } from '../core/Portal/Portal.js';
import { Overlay } from '../overlays/Overlay/Overlay.js';
import { Kbd } from '../core/Kbd/Kbd.js';
import { useReducedMotionMultiplier } from '../motion/hooks/useReducedMotionMultiplier.js';
import './Spotlight.css';

export interface SpotlightProps {
  /** Keyboard chord(s) that toggle the palette. Default `['mod+k', 'mod+p']`. */
  shortcut?: string | string[];
  /** Placeholder text in the search input. */
  placeholder?: string;
  /** Empty-state shown when no command matches. */
  nothingFound?: ReactNode;
  /** Render commands without a registered `group` under this label. Default `'General'`. */
  ungroupedLabel?: string;
  /** Show command's keyboard shortcut on the right. */
  showShortcut?: (cmd: PaletteCommand) => string | undefined;
  /** Group order: when omitted, groups render in registration order. */
  groupOrder?: string[];
  /** Custom render for empty palette (no commands at all). */
  empty?: ReactNode;
  /** Custom width. */
  width?: number | string;
  zIndex?: number;
  /** Render into a different portal target. */
  portalTarget?: HTMLElement | string;
}

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="8" cy="8" r="5.5" />
    <path d="M16 16l-3.5-3.5" />
  </svg>
);

/**
 * Command palette UI bound to sdk-utils' `usePaletteStore`. Registered
 * commands from anywhere in the app appear here automatically: register
 * via `useRegisterCommand()` or `usePaletteStore.getState().register()`.
 *
 *   import { Spotlight } from '@repletoryreach/sdk-ui/spotlight';
 *   import { useRegisterCommand } from '@repletoryreach/sdk-utils/hooks';
 *
 *   <Spotlight />
 *
 *   useRegisterCommand({
 *     id: 'inbox.open',
 *     title: 'Open inbox',
 *     keywords: ['conversations', 'messages'],
 *     run: () => nav('/inbox')
 *   });
 */
export function Spotlight({
  shortcut = ['mod+k', 'mod+p'],
  placeholder = 'Search commands…',
  nothingFound = 'No matching commands',
  ungroupedLabel = 'General',
  showShortcut,
  groupOrder,
  empty,
  width = 560,
  zIndex,
  portalTarget
}: SpotlightProps): ReactNode {
  const open = usePaletteStore((s) => s.open);
  const query = usePaletteStore((s) => s.query);
  const setQuery = usePaletteStore((s) => s.setQuery);
  const toggle = usePaletteStore((s) => s.toggle);
  const close = usePaletteStore((s) => s.closePalette);
  // Read raw commands and filter ourselves to avoid the store reading
  // `query` inside `filtered()` (which would create a fresh array on every
  // render and confuse React's render-phase comparison).
  const commands = usePaletteStore((s) => s.commands);
  const inputId = useStableId('rr-spotlight');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const multiplier = useReducedMotionMultiplier();

  const shortcuts = Array.isArray(shortcut) ? shortcut : [shortcut];

  // Toggle on mod+k (and any others). useShortcut from sdk-utils handles
  // chord parsing + the global keydown listener.
  for (const chord of shortcuts) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useShortcut(chord, (e) => { e.preventDefault(); toggle(); });
  }

  // Escape closes from within the palette.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Focus the input on open; reset query/active on close.
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 16);
      setActiveIdx(0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter((c) =>
      c.title.toLowerCase().includes(q) ||
      c.subtitle?.toLowerCase().includes(q) ||
      c.keywords?.some((k) => k.toLowerCase().includes(q))
    );
  }, [commands, query]);

  // Group commands.
  const groups = useMemo(() => {
    const out = new Map<string, PaletteCommand[]>();
    for (const c of filtered) {
      const g = c.group ?? ungroupedLabel;
      if (!out.has(g)) out.set(g, []);
      out.get(g)!.push(c);
    }
    if (groupOrder) {
      const ordered = new Map<string, PaletteCommand[]>();
      for (const g of groupOrder) if (out.has(g)) ordered.set(g, out.get(g)!);
      for (const [g, cmds] of out) if (!ordered.has(g)) ordered.set(g, cmds);
      return ordered;
    }
    return out;
  }, [filtered, ungroupedLabel, groupOrder]);

  // Flat ordered list used for keyboard navigation.
  const flatList = useMemo(() => [...groups.values()].flat(), [groups]);

  // Clamp active index when the list shrinks.
  useEffect(() => {
    if (activeIdx >= flatList.length) setActiveIdx(0);
  }, [flatList.length, activeIdx]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % Math.max(1, flatList.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + flatList.length) % Math.max(1, flatList.length));
    } else if (e.key === 'Enter') {
      const cmd = flatList[activeIdx];
      if (cmd) {
        e.preventDefault();
        cmd.run();
        close();
      }
    }
  };

  if (!open) return null;

  return (
    <Portal target={portalTarget}>
      <div
        className="rr-spotlight-root"
        style={{ zIndex: zIndex ?? 'var(--rr-z-modal)' as unknown as number }}
        onKeyDown={onKeyDown}
      >
        <Overlay backgroundOpacity={0.45} blur={6} onClick={close} />
        <div
          className="rr-spotlight"
          role="dialog"
          aria-modal="true"
          aria-labelledby={inputId}
          style={{
            width: typeof width === 'number' ? `${width}px` : width,
            animationDuration: multiplier === 0 ? '0ms' : undefined
          }}
        >
          <div className="rr-spotlight__search">
            <SearchIcon />
            <input
              ref={inputRef}
              id={inputId}
              type="search"
              placeholder={placeholder}
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              autoComplete="off"
              spellCheck={false}
            />
            <Kbd>esc</Kbd>
          </div>

          <div className="rr-spotlight__list" role="listbox">
            {flatList.length === 0 ? (
              <div className="rr-spotlight__empty">{commands.length === 0 ? (empty ?? nothingFound) : nothingFound}</div>
            ) : (
              [...groups.entries()].map(([group, cmds]) => (
                <div key={group} className="rr-spotlight__group">
                  <div className="rr-spotlight__group-label">{group}</div>
                  {cmds.map((cmd) => {
                    const idx = flatList.indexOf(cmd);
                    const active = idx === activeIdx;
                    return (
                      <button
                        key={cmd.id}
                        role="option"
                        aria-selected={active}
                        data-active={active || undefined}
                        type="button"
                        className="rr-spotlight__item"
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => { cmd.run(); close(); }}
                      >
                        {cmd.icon ? <span className="rr-spotlight__item-icon" aria-hidden>{cmd.icon}</span> : null}
                        <span className="rr-spotlight__item-body">
                          <span className="rr-spotlight__item-title">{cmd.title}</span>
                          {cmd.subtitle ? <span className="rr-spotlight__item-subtitle">{cmd.subtitle}</span> : null}
                        </span>
                        {showShortcut ? (() => {
                          const k = showShortcut(cmd);
                          return k ? <Kbd>{k}</Kbd> : null;
                        })() : null}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}
